import { useEffect, useRef, useState } from 'react';
import { NextPage, GetStaticProps } from 'next';
import Link from 'next/link';
import { Result, Button } from 'antd';
import styled from 'styled-components';

import Office from '../backend/s3/office';
import Flat from '../backend/s3/flat';
import { IOffice } from '../common/model/office.model';
import { IFlat } from '../common/model/flat.model';
import useI18n from '../common/hooks/useI18n';
import useSearchService from '../common/hooks/useSearchService';
import {
  BuyHouseOption,
  computeBuyHouseOptions,
} from '../common/helpers/buyHouseOption.utils';
import { deserializeMultiple } from '../common/helpers/serialization';

import { Header, Footer, FlatsDisplay, Head } from '../components/shared';

interface StaticProps {
  offices: IOffice[];
  buyHouseOptions: BuyHouseOption[];
  serializedFlats: string;
}

type Props = StaticProps;

const Layout = styled.div`
  display: flex;
  flex: auto;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 0;

  font-style: ${(props) => props.theme.font.style};
`;

const Content = styled.main`
  position: relative;
  flex: auto;
  margin-top: ${(props) => props.theme.headerHeight};
  background-color: ${(props) => props.theme.colors.grey};

  @media ${(props) => props.theme.breakpoints.mdd} {
    margin-top: 0;
  }
`;

const Page404: NextPage<Props> = ({
  offices,
  buyHouseOptions,
  serializedFlats,
}) => {
  const i18n = useI18n();
  const [recommendedFlats, setRecommendedFlats] = useState([] as IFlat[]);

  const flats = deserializeMultiple<IFlat>(serializedFlats);

  const searchServiceRef = useRef(useSearchService(flats));

  useEffect(() => {
    setRecommendedFlats(searchServiceRef.current.getLastSearchsResults());
  }, []);

  return (
    <Layout>
      <Head
        title={i18n.t('404Page.metaTitle')}
        description={i18n.t('404Page.metaDescription')}
      />

      <Header offices={offices} buyHouseOptions={buyHouseOptions} />

      <Content>
        <Result
          status="404"
          title="404"
          subTitle={i18n.t('404Page.message')}
          extra={
            <Link href="/" legacyBehavior>
              <a>
                <Button type="primary">{i18n.t('404Page.buttonText')}</Button>
              </a>
            </Link>
          }
        />

        <FlatsDisplay
          flats={recommendedFlats}
          offices={offices}
          title={i18n.t('404Page.suggestedFlats')}
          arrows={true}
        />
      </Content>

      <Footer />
    </Layout>
  );
};

export const getStaticProps: GetStaticProps<StaticProps> = async () => {
  const offices = await Office.getOffices();
  const [flats, searchOptions] = await Flat.getFlats();
  const serializedFlats = Flat.serialize(flats);
  const buyHouseOptions = computeBuyHouseOptions(searchOptions);

  return {
    props: {
      offices,
      buyHouseOptions,
      serializedFlats,
    },
  };
};

export default Page404;
